import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { apiFetch } from '../lib/api'
import type { CameraHealth } from '../types'

/**
 * Fetches camera health statuses from DB and optionally polls
 * the health-check endpoint to refresh them.
 */
export function useCameraHealth(orgId: string | null) {
  const [health, setHealth] = useState<Map<string, CameraHealth>>(new Map())
  const [loading, setLoading] = useState(false)

  // Load existing health records from DB
  const load = useCallback(async () => {
    if (!orgId) return
    const { data } = await supabase
      .from('camera_health')
      .select('*')
      .eq('org_id', orgId)

    if (data) {
      const map = new Map<string, CameraHealth>()
      for (const row of data) map.set(row.camera_id, row as CameraHealth)
      setHealth(map)
    }
  }, [orgId])

  useEffect(() => { load() }, [load])

  // Trigger a fresh health check for all cameras
  const refreshAll = useCallback(async () => {
    setLoading(true)
    try {
      await apiFetch('/api/camera/health-check', { method: 'POST', body: JSON.stringify({}) })
      await load()
    } finally {
      setLoading(false)
    }
  }, [load])

  // Check a single camera
  const refreshOne = useCallback(async (cameraId: string) => {
    await apiFetch('/api/camera/health-check', {
      method: 'POST',
      body: JSON.stringify({ cameraId }),
    })
    await load()
  }, [load])

  const getStatus = useCallback((cameraId: string) => {
    return health.get(cameraId) ?? null
  }, [health])

  return { health, loading, refreshAll, refreshOne, getStatus, reload: load }
}
